<template>
  <app-layout>
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        学校管理 ユーザ登録タスク詳細
      </h2>
    </template>

    <div class="">
      <div class="pt-4 px-12 flex justify-between items-center">
        <div class="flex">
          <div class="my-2 pr-3">
            <a
              class="py-3 px-6 bg-blue-600 hover:shadow-lg text-white rounded"
              :href="route('usersRegistration')">登録画面へ</a>
          </div>
          <div class="my-2">
            <a
              class="py-3 px-2 bg-blue-600 hover:shadow-lg text-white rounded"
              :href="route('usersRegistration.task')">登録タスク一覧へ</a>
          </div>
        </div>
        <button class="py-3 px-2 block col-span-4 bg-red-600 hover:shadow-lg text-white rounded">
          <download-csv
            :data="getTaskList"
            :labels="listHeader"
            :name="listFileName">
            タスク詳細リスト出力
          </download-csv>
        </button>
      </div>

      <div
        v-if="userRegistrationTask.status === 3 && userRegistrationTask.failed_reason"
        class="mx-12 my-4 p-4 bg-red-50 border border-red-200 rounded">
        <p class="text-red-700 font-semibold">タスク失敗理由:</p>
        <p class="text-red-600 mt-1">{{ userRegistrationTask.failed_reason }}</p>
      </div>

      <div
        v-if="!users.length"
        class="text-center">
        ユーザ情報がありません。
      </div>
      <div
        v-else
        class="py-4 flex flex-col items-center">
        <div class="shadow w-11/12 overflow-x-auto overflow-y-auto border-b border-gray-200 sm:rounded-lg">
          <table class="w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                  氏名
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                  メールアドレス
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                  学籍番号
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                  学科
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                  学年
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                  ステータス
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                  エラー内容
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                  登録完了メール送信状況
                </th>
              </tr>
            </thead>
            <tbody
              v-for="(item, i) in users"
              :key="i"
              class="bg-white divide-y divide-gray-200">
              <tr>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="text-gray-900">
                    {{ item.user_id ? item.user_id : '未登録' }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="text-gray-900">
                    {{ item.name }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span class="text-gray-900">
                    {{ item.email }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span class="text-gray-900">
                    {{ item.student_id }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span class="px-2 inline-flex leading-5 rounded-full text-gray-900">
                    {{ item.department }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span class="px-2 inline-flex leading-5 rounded-full text-gray-900">
                    {{ item.grade }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span class="px-2 inline-flex leading-5 rounded-full text-gray-900">
                    {{ getStatus(item.status) }}
                  </span>
                </td>
                <td
                  class="px-4 py-4 text-center max-w-[120px] truncate"
                  :title="item.error_message">
                  <span class="px-2 inline-flex leading-5 rounded-full text-gray-900">
                    {{ item.error_message }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span class="px-2 inline-flex leading-5 rounded-full text-gray-900">
                    {{ item.is_send_email ? '送信成功' : '送信失敗/未送信' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </app-layout>
</template>

<script setup>
import DownloadCsv from 'vue-json-csv'
import moment from 'moment'
import { computed } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'

const props = defineProps({
  school: {
    type: Object,
    default: () => ({}),
  },
  userRegistrationTask: {
    type: Object,
    default: () => ({}),
  },
  users: {
    type: Array,
    default: () => [],
  },
})

const getTaskList = computed(() => {
  const csvData = []
  props.users.forEach((user) => {
    csvData.push([
      user.user_id,
      user.name,
      user.email,
      user.student_id,
      user.department,
      user.grade,
      user.temp_pass,
      getStatus(user.status),
      user.error_message,
      user.is_send_email ? '送信成功' : '送信失敗/未送信',
    ])
  })
  return csvData
})

const schoolName = props.school ? props.school.name : 'generalUser'
const listFileName = `taskList_${
  schoolName
}_${
  moment(props.userRegistrationTask.created_at).format('yyyyMMDDHHmm')
}.csv`

const listHeader = ['user_id', 'name', 'email', 'student_id', 'department', 'grade', 'temp_pass', 'status', 'error_message', 'send_email_status']

const getStatus = (status) => {
  let str = ''
  switch (status) {
    case 1:
      str = '成功'
      break
    case 2:
      str = 'メールアドレス登録済みエラー'
      break
    case 3:
      str = 'その他エラー'
      break
    case 4:
      str = '既存アカウントに学校情報追加'
      break
    default:
      break
  }
  return str
}

const onReturnClicked = () => {
  Inertia.get(route('usersRegistration.task'))
}
</script>
